<div class="employee-list-container p-6">
  <!-- Header Section -->
  <div class="mb-6">
    <div class="flex justify-between items-center mb-4">
      <h1 class="text-3xl font-bold text-gray-900">Employee Management</h1>
      <app-sl-button
        *ngIf="hasCreatePermission"
        variant="primary"
        prefixIcon="person-plus"
        (buttonClick)="addEmployee()">
        Add Employee
      </app-sl-button>
    </div>
    
    <!-- Search and Filter Bar -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div class="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        <!-- Search Input -->
        <div class="flex-1 min-w-0">
          <app-sl-search
            placeholder="Search employees by name, role, or department..."
            [value]="searchTerm()"
            (search)="onSearch($event)"
            (clear)="onClearSearch()">
          </app-sl-search>
        </div>
        
        <!-- Sort Button -->
        <div class="flex gap-2">
          <app-sl-button
            variant="default"
            size="medium"
            [prefixIcon]="getSortIcon()"
            (buttonClick)="onSortByName()">
            Sort by Name
          </app-sl-button>
        </div>
      </div>
    </div>
  </div>

  <!-- Loading State -->
  <div *ngIf="loading()" class="flex justify-center items-center py-12">
    <sl-spinner style="font-size: 2rem;"></sl-spinner>
    <span class="ml-3 text-gray-600">Loading employees...</span>
  </div>

  <!-- Error State -->
  <div *ngIf="error()" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
    <div class="flex items-center">
      <sl-icon name="exclamation-triangle" class="text-red-500 mr-2"></sl-icon>
      <span class="text-red-700">{{ error() }}</span>
    </div>
  </div>

  <!-- Employee List -->
  <div *ngIf="!loading() && !error()" class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
    <!-- Table Header -->
    <div class="bg-gray-50 px-6 py-3 border-b border-gray-200">
      <div class="grid grid-cols-12 gap-4 items-center text-sm font-medium text-gray-700">
        <div class="col-span-4">Employee</div>
        <div class="col-span-2">Role</div>
        <div class="col-span-2">Department</div>
        <div class="col-span-2">Status</div>
        <div class="col-span-1">Profile</div>
        <div class="col-span-1">Actions</div>
      </div>
    </div>

    <!-- Employee Rows -->
    <div class="divide-y divide-gray-200">
      <div 
        *ngFor="let employee of employees(); trackBy: trackByEmployeeId" 
        class="px-6 py-4 hover:bg-gray-50 transition-colors duration-150">
        
        <div class="grid grid-cols-12 gap-4 items-center">
          <!-- Employee Info -->
          <div class="col-span-4 flex items-center space-x-3">
            <app-sl-avatar
              [image]="employee.profileImage"
              [label]="getEmployeeInitials(employee.fullName)"
              size="medium"
              shape="circle">
            </app-sl-avatar>
            
            <div class="min-w-0 flex-1">
              <p class="text-sm font-medium text-gray-900 truncate">
                {{ employee.fullName }}
              </p>
              <p *ngIf="employee.email" class="text-sm text-gray-500 truncate">
                {{ employee.email }}
              </p>
            </div>
          </div>

          <!-- Role -->
          <div class="col-span-2">
            <span class="text-sm text-gray-900">{{ employee.role }}</span>
          </div>

          <!-- Department -->
          <div class="col-span-2">
            <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {{ employee.department }}
            </span>
          </div>

          <!-- Status -->
          <div class="col-span-2">
            <span
              class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
              [class.bg-green-100]="employee.status === 'Active'"
              [class.text-green-800]="employee.status === 'Active'"
              [class.bg-yellow-100]="employee.status === 'Pending'"
              [class.text-yellow-800]="employee.status === 'Pending'"
              [class.bg-red-100]="employee.status === 'Inactive'"
              [class.text-red-800]="employee.status === 'Inactive'"
              [class.bg-gray-100]="employee.status !== 'Active' && employee.status !== 'Pending' && employee.status !== 'Inactive'"
              [class.text-gray-800]="employee.status !== 'Active' && employee.status !== 'Pending' && employee.status !== 'Inactive'">
              {{ employee.status }}
            </span>
          </div>

          <!-- Profile View Button -->
          <div class="col-span-1">
            <app-sl-button
              variant="text"
              size="small"
              prefixIcon="eye"
              (buttonClick)="viewProfile(employee)">
            </app-sl-button>
          </div>

          <!-- Actions Dropdown -->
          <div class="col-span-1">
            <!-- <app-sl-dropdown
              [items]="getActionItems(employee)"
              triggerIcon="three-dots-vertical"
              triggerLabel=""
              triggerVariant="text"
              triggerSize="small"
              (itemSelected)="onActionSelected(employee, $event)">
            </app-sl-dropdown> -->
            <app-sl-dropdown
              [items]="actionItems"
              triggerIcon="three-dots-vertical"
              triggerLabel=""
              triggerVariant="text"
              triggerSize="small"
              (itemSelected)="onActionSelected(employee, $event)">
            </app-sl-dropdown>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div *ngIf="employees().length === 0" class="text-center py-12">
      <sl-icon name="people" style="font-size: 3rem;" class="text-gray-400 mb-4"></sl-icon>
      <h3 class="text-lg font-medium text-gray-900 mb-2">No employees found</h3>
      <p class="text-gray-500 mb-4">
        <span *ngIf="searchTerm()">No employees match your search criteria.</span>
        <span *ngIf="!searchTerm()">Get started by adding your first employee.</span>
      </p>
      <app-sl-button 
        *ngIf="!searchTerm()"
        variant="primary" 
        prefixIcon="person-plus">
        Add Employee
      </app-sl-button>
      <app-sl-button 
        *ngIf="searchTerm()"
        variant="default" 
        (buttonClick)="onClearSearch()">
        Clear Search
      </app-sl-button>
    </div>
  </div>

  <!-- Results Summary -->
  <div *ngIf="!loading() && !error() && employees().length > 0" class="mt-4 text-sm text-gray-600">
    Showing {{ employees().length }} employee{{ employees().length === 1 ? '' : 's' }}
    <span *ngIf="searchTerm()"> matching "{{ searchTerm() }}"</span>
  </div>
</div>
